import { FC } from 'react';
import styled from 'styled-components';
import MainContainer from '../../ui_components/MainContainer';
import { useNavigate } from 'react-router-dom';
import SingleCardWrestlerMobile from './SingleCardWrestlerMobile';
import colors from '../../../styles/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPerson,
    faPersonDress,
    faSuitcase,
    faArrowLeft,
    faArrowRight,
    faArrowDownWideShort,
    faArrowUpWideShort,
} from '@fortawesome/free-solid-svg-icons';
import useSwipeGesture from '../../Hooks/useSwipeGesture';
import SearchPpv from '../../ppv_components/SearchPpv';
import type { Wrestler } from '../../../types';

// ─── Styled ────────────────────────────────────────────────────────────────────

const InnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const SearchRow = styled.div`
    padding: 6px 10px 0;
`;

const FiltersRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 10px 4px;
`;

const Chip = styled.button<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    border-radius: 4px;
    border: 1px solid ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.15)')};
    background-color: ${({ $active }) => ($active ? 'rgba(255,215,0,0.12)' : 'transparent')};
    color: ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.5)')};
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.15s, background-color 0.15s, color 0.15s;

    & .chip-icon {
        font-size: 11px;
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 28px;
    background-color: rgba(255, 255, 255, 0.1);
    align-self: center;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    padding: 8px 10px;
`;

const PaginationRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    padding: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const PaginationBtn = styled.button<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 6px;
    border: 1px solid ${({ disabled }) => (disabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.25)')};
    background-color: transparent;
    color: ${({ disabled }) => (disabled ? 'rgba(255,255,255,0.2)' : 'white')};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    font-size: 13px;
    transition: background-color 0.15s;

    &:not([disabled]):active {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const PaginationLabel = styled.span`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    min-width: 80px;
    text-align: center;
`;

// ─── Props ─────────────────────────────────────────────────────────────────────

interface ListWrestlerMobileRenderProps {
    listWrestlers: Wrestler[];
    isLoading: boolean;
    maxPageApi: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    isOrderByName: boolean;
    isOrderByHeight: boolean;
    isOrderByWeight: boolean;
    isFilterMan: boolean;
    isFilterWomen: boolean;
    isFilterManager: boolean;
    isOrderDesc: boolean;
    onOrderChange: (order: string) => void;
    onFilterChange: (filter: string) => void;
    onOrderDescChange: (isDesc: boolean) => void;
    onSearchChange: (text: string) => void;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const ListWrestlerMobileRender: FC<ListWrestlerMobileRenderProps> = ({
    listWrestlers,
    isLoading,
    maxPageApi,
    currentPage,
    onPageChange,
    isOrderByName,
    isOrderByHeight,
    isOrderByWeight,
    isFilterMan,
    isFilterWomen,
    isFilterManager,
    isOrderDesc,
    onOrderChange,
    onFilterChange,
    onOrderDescChange,
    onSearchChange,
}) => {
    const navigate = useNavigate();

    const swipeHandlers = useSwipeGesture(
        () => { if (currentPage < maxPageApi) onPageChange(currentPage + 1); },
        () => { if (currentPage > 1) onPageChange(currentPage - 1); },
        50
    );

    return (
        <MainContainer
            className='main-container'
            isMobile={true}
            padding='0'
            {...swipeHandlers}
        >
            <InnerContainer>
                {/* Barra ricerca */}
                <SearchRow>
                    <SearchPpv onFilterTextChange={onSearchChange} />
                </SearchRow>

                {/* Filtri genere */}
                <FiltersRow>
                    <Chip $active={isFilterMan} onClick={() => onFilterChange('man')}>
                        <FontAwesomeIcon icon={faPerson} className='chip-icon' /> Uomo
                    </Chip>
                    <Chip $active={isFilterWomen} onClick={() => onFilterChange('woman')}>
                        <FontAwesomeIcon icon={faPersonDress} className='chip-icon' /> Donna
                    </Chip>
                    <Chip $active={isFilterManager} onClick={() => onFilterChange('manager')}>
                        <FontAwesomeIcon icon={faSuitcase} className='chip-icon' /> Manager
                    </Chip>

                    <Divider />

                    {/* Ordine */}
                    <Chip $active={isOrderByName} onClick={() => onOrderChange('name')}>Nome</Chip>
                    <Chip $active={isOrderByHeight} onClick={() => onOrderChange('height')}>Altezza</Chip>
                    <Chip $active={isOrderByWeight} onClick={() => onOrderChange('weight')}>Peso</Chip>

                    <Chip onClick={() => onOrderDescChange(!isOrderDesc)}>
                        <FontAwesomeIcon
                            icon={isOrderDesc ? faArrowDownWideShort : faArrowUpWideShort}
                            className='chip-icon'
                        />
                    </Chip>
                </FiltersRow>

                {/* Griglia carte wrestler */}
                <CardsGrid>
                    {listWrestlers.map((wrestler, index) => (
                        <SingleCardWrestlerMobile
                            key={index}
                            wrestler={wrestler}
                            onClick={() => navigate(`/wrestlers/i_${wrestler.id}_d_${wrestler.name}`)}
                        />
                    ))}
                </CardsGrid>

                {/* Paginazione */}
                <PaginationRow>
                    <PaginationBtn
                        disabled={currentPage <= 1}
                        onClick={() => { if (currentPage > 1) onPageChange(currentPage - 1); }}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </PaginationBtn>
                    <PaginationLabel>Pag. {currentPage} / {maxPageApi}</PaginationLabel>
                    <PaginationBtn
                        disabled={currentPage >= maxPageApi}
                        onClick={() => { if (currentPage < maxPageApi) onPageChange(currentPage + 1); }}
                    >
                        <FontAwesomeIcon icon={faArrowRight} />
                    </PaginationBtn>
                </PaginationRow>
            </InnerContainer>
        </MainContainer>
    );
};

export default ListWrestlerMobileRender;
